import React from 'react';
import { 
  Clock, 
  ExternalLink, 
  X, 
  Check,
  BookOpen,
  FileText,
  Award,
  MessageSquare,
  Settings,
  CreditCard,
  GraduationCap,
  AlertCircle,
  Info,
  CheckCircle
} from 'lucide-react';

const NotificationItem = ({ notification, onMarkAsRead, onDelete, onAction }) => {
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'À l\'instant';
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} min`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `Il y a ${diffInHours}h`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `Il y a ${diffInDays} jour${diffInDays > 1 ? 's' : ''}`;
    
    return notificationTime.toLocaleDateString('fr-FR');
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'border-red-500 bg-red-50';
      case 'high':
        return 'border-orange-500 bg-orange-50';
      case 'medium':
        return 'border-blue-500 bg-blue-50';
      case 'low':
        return 'border-gray-300 bg-gray-50';
      default:
        return 'border-gray-300 bg-white';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'high':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      case 'medium':
        return <Info className="w-4 h-4 text-blue-500" />;
      case 'low':
        return <CheckCircle className="w-4 h-4 text-gray-500" />;
      default:
        return <Info className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'course_update':
      case 'new_lesson':
        return <BookOpen className="w-5 h-5 text-blue-600" />;
      case 'assignment_due':
      case 'grade_available':
        return <FileText className="w-5 h-5 text-green-600" />;
      case 'certificate_ready':
        return <Award className="w-5 h-5 text-yellow-600" />;
      case 'chat_message':
        return <MessageSquare className="w-5 h-5 text-purple-600" />;
      case 'system_announcement':
        return <Settings className="w-5 h-5 text-gray-600" />;
      case 'payment_success':
      case 'subscription_expiry':
        return <CreditCard className="w-5 h-5 text-green-600" />;
      case 'course_recommendation':
        return <GraduationCap className="w-5 h-5 text-indigo-600" />;
      default:
        return <Info className="w-5 h-5 text-gray-600" />;
    }
  };

  const handleAction = () => {
    if (onAction) {
      onAction(notification);
    }
    if (!notification.read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  return (
    <div className={`border-l-4 p-4 bg-white hover:bg-gray-50 transition-colors duration-200 ${
      notification.read ? 'opacity-75' : ''
    } ${getPriorityColor(notification.priority)}`}>
      <div className="flex items-start gap-3">
        {/* Icône de type */}
        <div className="flex-shrink-0 mt-1">
          {getTypeIcon(notification.type)}
        </div>

        {/* Contenu principal */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className={`font-medium ${notification.read ? 'text-gray-600' : 'text-gray-900'}`}>
                  {notification.title}
                </h4>
                {!notification.read && (
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                )}
                {getPriorityIcon(notification.priority)}
              </div>
              
              <p className={`text-sm mb-2 ${notification.read ? 'text-gray-500' : 'text-gray-700'}`}>
                {notification.message}
              </p>

              {/* Métadonnées */}
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatTimeAgo(notification.timestamp)}
                </div>
                
                {notification.category && (
                  <span className="px-2 py-1 bg-gray-100 rounded-full">
                    {notification.category}
                  </span>
                )}
                
                {notification.courseName && (
                  <span className="truncate max-w-32">
                    {notification.courseName}
                  </span>
                )}
                
                {notification.grade && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
                    Note: {notification.grade}/100
                  </span>
                )}
                
                {notification.amount && (
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
                    €{notification.amount}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              {!notification.read && (
                <button
                  onClick={() => onMarkAsRead && onMarkAsRead(notification.id)}
                  className="p-1 text-gray-400 hover:text-green-600 transition-colors duration-200"
                  title="Marquer comme lu"
                >
                  <Check className="w-4 h-4" />
                </button>
              )}
              
              <button
                onClick={() => onDelete && onDelete(notification.id)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                title="Supprimer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Action principale */}
          {notification.actionUrl && notification.actionText && (
            <div className="mt-3">
              <button
                onClick={handleAction}
                className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 transition-colors duration-200"
              >
                <ExternalLink className="w-4 h-4" />
                {notification.actionText}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
